import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Maximize2, Minimize2 } from "lucide-react";

const pad = (n) => String(n).padStart(3, "0");

export const Flipbook = ({ open, page, pageCount, onClose, onChange }) => {
  const [cur, setCur] = useState(1);
  const [immersive, setImmersive] = useState(false);
  const [pageInput, setPageInput] = useState("1");
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" && window.innerWidth < 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (open) {
      setImmersive(false);
      setCur(page <= 1 ? 1 : isMobile ? page : page % 2 === 0 ? page - 1 : page);
    }
  }, [open, page, isMobile]);

  const step = isMobile ? 1 : 2;
  const prev = () => setCur((c) => Math.max(1, c - step));
  const next = () => setCur((c) => Math.min(pageCount, c + step));
  const jump = (v) => setCur(Number(v));

  useEffect(() => {
    setPageInput(String(cur));
  }, [cur]);

  const goTo = () => {
    const v = parseInt(pageInput, 10);
    if (isNaN(v)) return;
    const clamped = Math.min(pageCount, Math.max(1, v));
    setCur(isMobile ? clamped : clamped % 2 === 0 ? Math.max(1, clamped - 1) : clamped);
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, pageCount, isMobile, cur]);

  if (!open) return null;
  const spread = !isMobile && cur !== 1;
  const right = cur + 1;

  return (
    <motion.div
      className="fixed inset-0 z-[100] bg-black/95 flex flex-col"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      data-testid="flipbook-overlay"
    >
      <div
        className={`flex items-center justify-between px-4 md:px-8 h-14 md:h-16 border-b border-white/10 shrink-0 transition-opacity duration-300 ${immersive ? "hidden" : ""}`}
      >
        <p className="font-accent uppercase tracking-[0.3em] text-white/60 text-xs md:text-sm">
          Catalogue Tremblay 2026
        </p>
        <p className="hidden sm:block font-mono text-xs text-white/50" data-testid="flipbook-indicator">
          {spread ? `Pages ${cur}–${right} / ${pageCount}` : `Page ${cur} / ${pageCount}`}
        </p>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setImmersive(true)}
            data-testid="flipbook-fullscreen"
            className="text-white/70 hover:text-blaze transition-colors p-2"
            aria-label="Plein écran"
          >
            <Maximize2 className="h-5 w-5 md:h-6 md:w-6" />
          </button>
          <button
            onClick={onClose}
            data-testid="flipbook-close"
            className="text-white/70 hover:text-blaze transition-colors p-2"
            aria-label="Fermer"
          >
            <X className="h-5 w-5 md:h-6 md:w-6" />
          </button>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center gap-1 px-1 md:px-8 py-2 md:py-4 min-h-0 relative">
        <AnimatePresence mode="wait">
          <motion.div
            key={cur}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="flex items-center justify-center h-full w-full gap-1"
          >
            <img
              src={`/catalog/pages/page_${pad(cur)}.jpg`}
              alt={`Page ${cur} du catalogue Tremblay 2026`}
              onClick={() => setImmersive((v) => !v)}
              className="h-full w-auto max-w-full sm:max-w-[50%] object-contain cursor-zoom-in shadow-[0_0_60px_rgba(0,0,0,0.8)]"
              data-testid="flipbook-page-left"
            />
            {spread && right <= pageCount && (
              <img
                src={`/catalog/pages/page_${pad(right)}.jpg`}
                alt={`Page ${right} du catalogue Tremblay 2026`}
                className="hidden md:block h-full w-auto max-w-[50%] object-contain shadow-[0_0_60px_rgba(0,0,0,0.8)]"
                data-testid="flipbook-page-right"
              />
            )}
          </motion.div>
        </AnimatePresence>

        {immersive && (
          <button
            onClick={() => setImmersive(false)}
            data-testid="flipbook-exit-fullscreen"
            className="absolute top-3 right-3 z-10 p-2.5 bg-black/70 border border-white/20 text-white/80 hover:text-blaze transition-colors"
            aria-label="Quitter le plein écran"
          >
            <Minimize2 className="h-5 w-5" />
          </button>
        )}
      </div>

      <div
        className={`h-14 md:h-16 border-t border-white/10 flex items-center justify-center gap-4 px-4 shrink-0 transition-opacity duration-300 ${immersive ? "hidden" : ""}`}
      >
        <input
          type="number"
          min="1"
          max={pageCount}
          value={pageInput}
          onChange={(e) => setPageInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && goTo()}
          data-testid="flipbook-page-input"
          className="w-14 md:w-16 bg-zinc-900 border border-white/20 text-white text-center font-mono text-xs py-2 focus:border-blaze focus:outline-none"
          aria-label="Numéro de page"
        />
        <button
          onClick={goTo}
          data-testid="flipbook-page-go"
          className="font-accent uppercase tracking-[0.15em] text-[11px] px-3 py-2 border border-white/20 text-white/80 hover:border-blaze hover:text-blaze transition-colors"
        >
          Aller
        </button>
        <button
          onClick={prev}
          disabled={cur === 1}
          data-testid="flipbook-prev"
          className="p-2 text-white/70 hover:text-blaze disabled:opacity-25 disabled:hover:text-white/70 transition-colors"
          aria-label="Page précédente"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
        <input
          type="range"
          min="1"
          max={pageCount}
          value={cur}
          onChange={(e) => jump(e.target.value)}
          data-testid="flipbook-slider"
          className="w-32 md:w-72 accent-[#FF2E00]"
          aria-label="Aller à la page"
        />
        <button
          onClick={next}
          disabled={cur >= pageCount}
          data-testid="flipbook-next"
          className="p-2 text-white/70 hover:text-blaze disabled:opacity-25 disabled:hover:text-white/70 transition-colors"
          aria-label="Page suivante"
        >
          <ChevronRight className="h-6 w-6" />
        </button>
      </div>
    </motion.div>
  );
};
